import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  ViewChild
} from "@angular/core";
import { AbstractControl, NonNullableFormBuilder, ValidatorFn, Validators } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Subscription } from "rxjs";
import { RecordingTypesEnum } from "@onsip/web/features/shared/components/togglableRecorder/config/recording-types.enum";
import { TogglableRecorderComponent } from "@onsip/web/features/shared/components/togglableRecorder/togglable-recorder.component";
import { SnackbarService } from "@onsip/web/features/shared/components/snackbar/snackbar.service";
import { ModalMaterialComponent } from "@onsip/web/features/shared/components/modal/modal-material.component";
import { take } from "rxjs/operators";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { views } from "@onsip/web/app/phone/views";
import { RecordingService } from "@onsip/common/services/api/resources/recording/recording.service";
import { Recording } from "@onsip/common/services/api/resources/recording/recording";

@Component({
  selector: "onsip-recording-setup",
  templateUrl: "./recording-setup.component.html",
  styleUrls: ["./recording-setup.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class RecordingSetupComponent implements OnInit, OnDestroy {
  @HostBinding("class.onsip-admin-host-grid")
  @HostBinding("class.onsip-grid-content")
  _dontUse = true;
  @ViewChild("recordingControl") recorderRef!: TogglableRecorderComponent;

  constructor(
    private translate: TranslateService,
    private snackbar: SnackbarService,
    private dialog: MatDialog,
    private router: Router,
    private recordingService: RecordingService,
    private fb: NonNullableFormBuilder
  ) {}
  private existingRecordings: Array<Recording> = [];
  private unsubscriber = new Subscription();
  loadingStateComplete$ = new BehaviorSubject(true);

  views = views;
  recordingType: RecordingTypesEnum = RecordingTypesEnum.RECORD;
  form = this.fb.group({
    name: [
      "",
      [
        Validators.required,
        Validators.maxLength(60),
        Validators.pattern("^[0-9A-Za-z #'.-]+$"),
        this.repeatNameValidator()
      ]
    ],
    record: [<Recording | File | undefined>undefined, [Validators.required]]
  });

  ngOnInit(): void {
    this.recordingService.recordingBrowse().then(response => {
      if (response.status === "success") {
        this.existingRecordings = Object.values(response.data);
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  submitForm(): void {
    if (!this.form.controls.record.value) {
      this.showNoRecordingErrorSnackbar();
      return;
    }
    this.loadingStateComplete$.next(false);
    this.recorderRef
      .getRecNumber(
        this.form.controls.record.value,
        this.recordingType,
        this.form.controls.name.value
      )
      .then(recNum => {
        if (recNum) {
          this.snackbar.openSnackBar("Recording was created.", "success");
          this.router.navigate([this.views.ADMIN_RECORDING]);
        }
        this.loadingStateComplete$.next(true);
      });
  }

  onCancel(): void {
    this.unsubscriber.add(
      this.dialog
        .open(ModalMaterialComponent, {
          panelClass: ["mat-typography", "onsip-dialog-universal-style"],
          data: {
            title: "Cancel setup?",
            message: this.translate.instant(
              "ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"
            ),
            primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
            secondaryBtnText: this.translate.instant("ONSIP_I18N.CANCEL"),
            primaryBtnFlat: true
          }
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe(retObj => {
          if (retObj && retObj.doPrimaryAction) {
            this.router.navigate([this.views.ADMIN_RECORDING]);
          }
        })
    );
  }

  showNoRecordingErrorSnackbar(): void {
    this.snackbar.openSnackBar("Please record or upload recording.", "error");
  }

  getGenericRequiredError(control: AbstractControl | undefined | null, value: string): string {
    // eslint-disable-next-line no-null/no-null
    if (control === undefined || control === null) return "";
    if (control.hasError("required")) {
      return this.translate.instant("ONSIP_I18N._VALUE__IS_REQUIRED", { value });
    } else if (control.hasError("maxlength")) {
      return "Name is too long";
    } else if (control.hasError("pattern")) {
      return this.translate.instant("ONSIP_I18N.ONLY_LETTERS_NUMBERS_OR_THE_SYMBOLS__ACCEPTED");
    } else if (control.hasError("repeatName")) {
      return this.translate.instant("ONSIP_I18N.THE_FILE_NAME_ALREADY_EXISTS", {
        name: control?.value
      });
    }

    return "";
  }

  private repeatNameValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const fileName: string = control.value || "";

      return this.existingRecordings.some(({ name }) => name === fileName)
        ? { repeatName: true }
        : // eslint-disable-next-line no-null/no-null
          null;
    };
  }
}
